import { useMemo } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { customFieldEndpoints } from '@/lib/api/endpoints';
import { customFieldKeys } from '@/lib/queryKeys';
import type {
  CustomFieldBulkValuesResponse,
  CustomFieldEntityType,
  CustomFieldSchema,
} from '@/types/custom-fields';
import { useCustomFieldSchema } from './useCustomFieldSchema';

function normalizeEntityIds(entityIds: string[]) {
  return [...new Set(entityIds.filter(Boolean))].sort();
}

function normalizeSchema(data: CustomFieldSchema[] | undefined) {
  return (Array.isArray(data) ? data : []).filter(
    (schema) => schema.definition?.isActive && schema.association?.isActive,
  );
}

export async function fetchCustomFieldTableValues(
  entityType: CustomFieldEntityType,
  entityIds: string[],
) {
  const normalizedIds = normalizeEntityIds(entityIds);
  if (normalizedIds.length === 0) return {} as CustomFieldBulkValuesResponse;

  return apiGet<CustomFieldBulkValuesResponse>(
    operationsClient,
    customFieldEndpoints.bulkValues(),
    {
      params: {
        entityType,
        entityIds: normalizedIds.join(','),
      },
    },
  );
}

export function useCustomFieldTableValues(entityType: CustomFieldEntityType, entityIds: string[]) {
  const schemaQuery = useCustomFieldSchema(entityType);
  const normalizedEntityIds = normalizeEntityIds(entityIds);
  const schemas = useMemo(() => normalizeSchema(schemaQuery.data), [schemaQuery.data]);

  const valuesQuery = useApiQuery<CustomFieldBulkValuesResponse>({
    queryKey: customFieldKeys.bulkValues(entityType, normalizedEntityIds),
    queryFn: () => fetchCustomFieldTableValues(entityType, normalizedEntityIds),
    enabled: normalizedEntityIds.length > 0,
  });

  return {
    schemas,
    valuesByEntityId: valuesQuery.data ?? {},
    isLoading: schemaQuery.isLoading || valuesQuery.isLoading,
    isError: schemaQuery.isError || valuesQuery.isError,
  };
}
